// src/lib/constants/buildReadiness.ts
//
// Build readiness (3.34.5; owner ruling 2026-09-25): before the click, the
// Status tab asks the factory the question its build admission answers — "can
// this app build now?" — and renders the unmet prerequisites as a checklist,
// with Build app off while any is unmet. The factory stays the one authority:
// the dashboard renders its tokens (the same ones a refused request carries,
// FACTORY_BUILD_REFUSALS) and never re-derives the rules. One design for every
// platform section (Roku, Fire TV, Google TV, Apple TV later).

import { APP_FACTORY_QUERY_KEYS } from './appFactory';

/** Where an unmet item is fixed: a sub-tab of the platform, the address flow, the Content page, or us. */
export const BUILD_READINESS_FIX_KINDS = ['images', 'address', 'content', 'settings', 'support'] as const;
export type BuildReadinessFixKind = (typeof BUILD_READINESS_FIX_KINDS)[number];

export interface BuildReadinessRowInfo {
  /** The row's label; `{platform}` = the platform's label. */
  label: string;
  fix: BuildReadinessFixKind;
}

/**
 * The factory's admission tokens → the checklist row. A token not listed here
 * (a newer factory) still renders, with the factory's own sentence and no button.
 */
export const BUILD_READINESS_ROWS: Record<string, BuildReadinessRowInfo> = {
  missing_images: { label: 'Images', fix: 'images' },
  no_web_domain: { label: 'App address', fix: 'address' },
  no_content_tree: { label: 'Content', fix: 'content' },
  empty_content_tree: { label: 'Content', fix: 'content' },
  no_platform_title: { label: 'App name', fix: 'settings' },
  // The package name is assigned when the platform is enabled and only an operator
  // may change it, so its refusal sentence names the contact: a support row.
  no_store_package_id: { label: 'Package name', fix: 'support' },
  bad_store_package_id: { label: 'Package name', fix: 'support' },
  platform_inactive: { label: '{platform} app', fix: 'settings' },
  no_platform_row: { label: '{platform} app', fix: 'settings' },
  no_signing_key: { label: 'Signing key', fix: 'support' },
  release_not_for_platform: { label: 'App version', fix: 'support' },
  platform_not_buildable: { label: 'Build system', fix: 'support' },
};

/** The neutral row's token when the factory could not check the images (`imagesChecked: false`). */
export const BUILD_READINESS_IMAGES_UNCHECKED = 'images_unchecked';

export const BUILD_READINESS_COPY = {
  title: 'Readiness',
  toBuild: 'To build',
  toSubmit: 'To submit',
  ready: 'Ready to build',
  imagesUnchecked: 'Your images could not be checked right now; the build checks them again.',
  unknownLabel: 'Requirement',
  /** The fix button per kind; a support row has no button (its sentence names the contact). */
  fixLabels: {
    images: 'Open Images',
    address: 'Set up address',
    content: 'Open Content',
    settings: 'Open Settings',
  } as Partial<Record<BuildReadinessFixKind, string>>,
} as const;

/** Short-lived: a prerequisite moves on another sub-tab or page, and the read is cheap. */
export const BUILD_READINESS_STALE_MS = 10_000;

export const BUILD_READINESS_QUERY_KEYS = {
  /** Every readiness read of one app — what a mutation that changes a prerequisite invalidates. */
  app: (cId: number, appCfgId: number) =>
    [...APP_FACTORY_QUERY_KEYS.all, 'buildReadiness', cId, appCfgId] as const,
  /** One platform + release: a new release is a new question, never the old answer. */
  read: (cId: number, appCfgId: number, platform: string, shellReleaseId: string) =>
    [...BUILD_READINESS_QUERY_KEYS.app(cId, appCfgId), platform, shellReleaseId] as const,
} as const;
